import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, Col, Placeholder, Row } from 'reactstrap';
import { createSelector } from 'reselect';
import SimpleAccountCard from '../../../../Components/General/Widgets/SimpleAccountCard';
import CardWithError from '../../../../Components/Common/CardWithError';
import CardWithoutData from '../../../../Components/Common/CardWithoutData';
import { getGuidesInfoCompareManagement, getGuidesInfoCompareStatus, getGuidesInfoCompareType } from '../../../../slices/thunks';
import OPlaceholder from '../../../../Components/OElements/OPlaceholder';

function safeCalculatePercentageChange(current, previous) {
    if (current === undefined || previous === undefined) {
        return 0; // O algún valor por defecto o indicador de error
    }
    return calculatePercentageChange(current, previous);
}

const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current === 0 ? 0 : 100; // Manejar caso cuando el valor anterior es 0
    return ((current - previous) / previous) * 100;
};

function KPIlist({ id, um = '' }) {
    const dispatch = useDispatch();
    const [data, setData] = useState([]);

    const selectDataType = createSelector(
        state => state.ResidueDashboard,
        dashboard => ({
            dataType: dashboard.infoCompareType,
            isLoadingType: dashboard.isLoading,
            isSuccessType: dashboard.isSuccess,
            isErrorType: dashboard.isError,
        })
    );

    const selectDataStatus = createSelector(
        state => state.ResidueDashboard,
        dashboard => ({
            dataStatus: dashboard.infoCompareStatus,
            isLoadingStatus: dashboard.isLoading,
            isSuccessStatus: dashboard.isSuccess,
            isErrorStatus: dashboard.isError,
        })
    );

    const selectDataManagement = createSelector(
        state => state.ResidueDashboard,
        dashboard => ({
            dataManagement: dashboard.infoCompareManagement,
            isLoadingManagement: dashboard.isLoading,
            isSuccessManagement: dashboard.isSuccess,
            isErrorManagement: dashboard.isError,
        })
    );

    const { dataType, isLoadingType, isSuccessType, isErrorType } = useSelector(selectDataType);
    const { dataStatus, isLoadingStatus, isSuccessStatus, isErrorStatus } = useSelector(selectDataStatus);
    const { dataManagement, isLoadingManagement, isSuccessManagement, isErrorManagement } = useSelector(selectDataManagement);

    useEffect(() => {
        dispatch(getGuidesInfoCompareManagement(id));
        dispatch(getGuidesInfoCompareType(id));
        dispatch(getGuidesInfoCompareStatus(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (isSuccessType && dataType.length > 0) {
            const updatedDataType = dataType.map(item => {
                const { current_total, previous_total } = item;
                let percentage = null;

                if (previous_total !== 0) {
                    percentage = ((current_total - previous_total) / previous_total) * 100;
                } else if (previous_total === 0 && current_total !== 0) {
                    percentage = 100;
                }

                return { ...item, percentage, source: 'type' };
            });

            setData(prevData => {
                const newData = updatedDataType.filter(
                    newItem => !prevData.some(item => item.type === newItem.type)
                );
                return [...prevData, ...newData];
            });
        }
    }, [dataType, isSuccessType]);

    useEffect(() => {
        if (isSuccessStatus && dataStatus.length > 0) {
            let result = [];
            if (dataStatus[0]) {
                result.push({
                    type: "Declarados",
                    current_total: dataStatus[0].Declarados !== undefined ? dataStatus[0].Declarados : 0,
                    percentage: safeCalculatePercentageChange(dataStatus[0].Declarados, dataStatus[1] ? dataStatus[1].Declarados : undefined),
                    source: 'status'
                });

                result.push({
                    type: "No Declarados",
                    current_total: dataStatus[0]["No Declarados"] !== undefined ? dataStatus[0]["No Declarados"] : 0,
                    percentage: safeCalculatePercentageChange(dataStatus[0]["No Declarados"], dataStatus[1] ? dataStatus[1]["No Declarados"] : undefined),
                    source: 'status'
                });
            }

            setData(prevData => {
                const newData = result.filter(
                    newItem => !prevData.some(item => item.type === newItem.type)
                );
                return [...prevData, ...newData];
            });
        }
    }, [dataStatus, isSuccessStatus]);

    useEffect(() => {
        if (isSuccessManagement && dataManagement.length > 0) {
            const updatedDataManagement = dataManagement.map(item => ({ ...item, source: 'management' }));
            setData(prevData => {
                const newData = updatedDataManagement.filter(
                    newItem => !prevData.some(item => item.type === newItem.type)
                );
                return [...prevData, ...newData];
            });
        }
    }, [dataManagement, isSuccessManagement]);

    if (isLoadingType || isLoadingStatus || isLoadingManagement) {
        return <OPlaceholder />;
    }

    if (isErrorType || isErrorStatus || isErrorManagement) {
        return <CardWithError />;
    }

    if ((isSuccessType || isSuccessStatus || isSuccessManagement) && data.length > 0) {
        return (
            <React.Fragment>
                <Row>
                    {data.sort((a, b) => a.source.localeCompare(b.source)).map((item, index) => (
                        <Col key={index}>
                            <SimpleAccountCard
                                label={'Residuos ' + item.type}
                                suffix={' ' + um}
                                decimals={2}
                                counter={item.current_total}
                                percentage={item.percentage !== null ? item.percentage.toFixed(0) : 0}
                                caption="vs mes anterior"
                            />
                        </Col>
                    ))}
                </Row>
            </React.Fragment>
        );
    }

    return <CardWithoutData />;
}

export default KPIlist;
